/**
 * CSRF double-submit helper (Phase 5b) — shared by `services/api.ts`'s
 * axios client and `services/fleet.ts`'s fetch-based client. Kept as
 * its own small module (rather than folded into either) so neither
 * has to depend on the other — mirrors the role the now-deleted
 * `services/adminBearer.ts` used to play.
 *
 * The backend mints a readable (non-HttpOnly) `tuv_csrf` cookie
 * alongside every session cookie (`services/auth/csrf.py`) and
 * requires its value echoed back as the `X-Tuvion-CSRF` header on
 * every unsafe-method (POST/PUT/PATCH/DELETE) request from a real
 * cookie session — see that module's docstring for the full
 * rationale (double-submit: only same-origin JS can read a
 * non-HttpOnly cookie AND set a custom header, so this proves the
 * request isn't a forged cross-site one silently riding the ambient
 * session cookie). The cookie name is NOT environment-prefixed
 * (always literally `tuv_csrf`, dev and staging/prod alike — unlike
 * the session cookie's `__Host-` prefix, see `principal.py::
 * session_cookie_name`), so this reader needs no environment
 * awareness either.
 */

const CSRF_COOKIE_NAME = 'tuv_csrf';
export const CSRF_HEADER_NAME = 'X-Tuvion-CSRF';

/** HTTP methods that mutate state and therefore need the CSRF header
 *  (matches `services/auth/csrf.py`'s `_SAFE_METHODS` exclusion set,
 *  inverted). Lowercase — axios reports `config.method` lowercase;
 *  callers normalize their own method string before checking this. */
export const UNSAFE_METHODS: ReadonlySet<string> = new Set(['post', 'put', 'patch', 'delete']);

const _CSRF_COOKIE_RE = new RegExp(`(?:^|;\\s*)${CSRF_COOKIE_NAME}=([^;]+)`);

/** Read the `tuv_csrf` cookie's current value, or `undefined` if it
 *  isn't set (anonymous — nothing to attach). Safe to call from
 *  non-browser contexts (SSR/test) — returns `undefined`. */
export function readCsrfCookie(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(_CSRF_COOKIE_RE);
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Redirect to `/login`, preserving the current path+query so the
 * post-login flow can return here (`LoginPage` reads `?next=`). A
 * hard navigation (`window.location`, not react-router) deliberately:
 * this is called from axios/fetch interceptors that run outside any
 * React context, and a full reload also guarantees stale in-memory
 * app state (AuthSessionContext, component state built on the old
 * identity) is wiped after a session dies mid-use.
 */
export function redirectToLogin(): void {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith('/login')) return;
  const next = window.location.pathname + window.location.search;
  window.location.href = `/login?next=${encodeURIComponent(next)}`;
}
